import { TranslationsService } from '../services/translations.service';
import { CellColors, SidePaneColors } from '../model/colors.model';
import { ReportStaticValues } from '../services/reports.service';

export enum MissingDataValues {
  noData = 'noData',
  notAvailable = 'notAvailable',
  notApplicable = 'n/a',
  notApplicableAlt = 'na',
}

export const missingDataTranslations = {
  [MissingDataValues.noData]: ['REPORTDATA.noData', 'No Data'],
  [MissingDataValues.notAvailable]: ['REPORTDATA.notAvailable', 'Not Available'],
  [MissingDataValues.notApplicable]: ['REPORTDATA.na', 'N/A'],
  [MissingDataValues.notApplicableAlt]: ['REPORTDATA.na', 'N/A'],
};

export const noNegativeYesPositiveCssClass = (value: string): string => {
  return value?.toLowerCase() === 'yes' ? 'agGridGreenTexts' : 'agGridRedTexts';
};

export const noPositiveYesNegativeCssClass = (value: string): string => {
  return value.toLowerCase() === 'yes' ? 'agGridRedTexts' : 'agGridGreenTexts';
};

export const noAndNotAssignedNegativeCssClass = (value: string): string => {
  const valueLowerCase: string = value.toLowerCase?.() ?? value;

  if (valueLowerCase === ReportStaticValues.no || valueLowerCase === ReportStaticValues.notStarted) {
    return CellColors.red;
  }

  return CellColors.grey;
};

export const valueOrTranslateMissingData = (value: string, translations: TranslationsService): string => {
  if (missingDataTranslations[value]) {
    return translations.translate(missingDataTranslations[value][0], missingDataTranslations[value][1]);
  }

  return value;
};

export const percentageOrMissingData = (value: string, translations: TranslationsService): string => {
  if (missingDataTranslations[value]) {
    return translations.translate(missingDataTranslations[value][0], missingDataTranslations[value][1]);
  }

  return `${value}%`;
};

export const missingNumberAsNull = (value: MissingDataValues): number | null => {
  if (value === null) {
    return null;
  }

  if (Object.values(MissingDataValues).includes(value)) {
    return null;
  }

  return Number(value);
};

export const missingValueAsNull = (value: MissingDataValues): string | number | null => {
  if (Object.values(MissingDataValues).includes(value?.toLowerCase() as MissingDataValues)) {
    return null;
  }

  return value;
};

export const sortPositiveValuesWithMissingData = (valueA: any, valueB: any): number => {
  const map = {
    [MissingDataValues.noData]: -3,
    [MissingDataValues.notAvailable]: -2,
    [MissingDataValues.notApplicable]: -1,
  };

  valueA = map[valueA] ?? valueA;
  valueB = map[valueB] ?? valueB;

  return valueA > valueB ? 1 : -1;
};

export const sortDatesWithMissingValues = (valueA: any, valueB: any): number => {
  const map = {
    [MissingDataValues.noData]: new Date(-9999, 0, 0),
    [MissingDataValues.notAvailable]: new Date(-9998, 0, 0),
    [MissingDataValues.notApplicable]: new Date(-9997, 0, 0),
    null: new Date(-9996, 0, 0),
    '': new Date(-9996, 0, 0),
  };
  const mappedValueA = map[valueA?.toLowerCase?.() ?? valueA];
  const mappedValueB = map[valueB?.toLowerCase?.() ?? valueB];

  valueA = mappedValueA === undefined ? Date.parse(valueA) : mappedValueA;
  valueB = mappedValueB === undefined ? Date.parse(valueB) : mappedValueB;

  return valueA > valueB ? 1 : -1;
};

export const negativeOneAsNa = (value: number): string | number => {
  return value === -1 ? 'na' : value;
};

export const negativeOneAsNull = (value: number): string | number => {
  return value === -1 ? null : value;
};

export const convertAgGridColorToSidePaneColor = (className: string): string => {
  switch (className) {
    case CellColors.red:
      return SidePaneColors.red;
    case CellColors.orange:
    case CellColors.amber:
      return SidePaneColors.orange;
    case CellColors.green:
      return SidePaneColors.green;
    case CellColors.blue:
      return SidePaneColors.blue;
    case CellColors.black:
      return SidePaneColors.black;
    case CellColors.grey:
      return SidePaneColors.grey;
    default:
      return className;
  }
};
